// src/pages/Dealer/ViewDealers.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import dealerService from '../../services/dealer.service';
import Toast from '../../components/Toast/Toast';
import './ViewDealers.css';
import { useNavigate } from 'react-router-dom';

const ViewDealers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState(null);
  const limit = 10;

  // Confirm modal state — covers both approve and delete
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,        // 'approve' | 'delete'
    dealer: null,
    loading: false,
  });

  const isAdminRole = ['SUPER_ADMIN', 'OFFICE_ADMIN'].includes(user?.role);

  const fetchDealers = () => {
    const companyId = isAdminRole ? localStorage.getItem('selectedCompanyId') : null;

    if (isAdminRole && !companyId) {
      setError('Please select a company first');
      return;
    }

    setLoading(true);
    setError(null);

    dealerService.getAllDealers(companyId, page, limit, searchTerm)
      .then(response => {
        if (response.success) {
          setDealers(response.data.dealers);
          setTotal(response.data.pagination.total);
          setTotalPages(response.data.pagination.totalPages);
        }
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to fetch dealers');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchDealers(); }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, user]);

  useEffect(() => { setPage(1); }, [searchTerm]);

  // ── Confirm modal flow ──────────────────────────────────────────────────
  const askApprove = (dealer) => setConfirmState({ open: true, action: 'approve', dealer, loading: false });
  const askDelete  = (dealer) => setConfirmState({ open: true, action: 'delete',  dealer, loading: false });
  const cancelConfirm = () => {
    if (confirmState.loading) return;
    setConfirmState({ open: false, action: null, dealer: null, loading: false });
  };

  const runConfirm = async () => {
    setConfirmState(prev => ({ ...prev, loading: true }));
    try {
      if (confirmState.action === 'approve') {
        await dealerService.approveDealer(confirmState.dealer.id);
        setToast({ message: 'Dealer approved successfully', type: 'success' });
      } else if (confirmState.action === 'delete') {
        await dealerService.deleteDealer(confirmState.dealer.id);
        setToast({ message: 'Dealer deleted successfully', type: 'success' });
      }
      fetchDealers();
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Action failed',
        type: 'error',
      });
    } finally {
      setConfirmState({ open: false, action: null, dealer: null, loading: false });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // Status check — handle both casings ('PENDING' vs 'pending')
  const isPending = (d) => (d.status || '').toUpperCase() === 'PENDING';
  const isApproved = (d) => (d.status || '').toUpperCase() === 'APPROVED';
  const hasHQ = (d) => d.headquarter?.name || d.headquarterId;

  return (
    <div className="view-dealers-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="dealers-header">
        <h4 className="dealers-title">View Dealers</h4>
        <button onClick={() => navigate('/dealer/add')}>Add Dealer</button>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name, mobile..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dealers...</p>
        </div>
      ) : error ? (
        <div className="error-container"><p className="error-message">{error}</p></div>
      ) : dealers.length === 0 ? (
        <div className="empty-state"><p>No dealers found</p></div>
      ) : (
        <>
          <div className="table-container">
            <table className="dealers-table">
              <thead>
                <tr>
                  <th>SHOP NAME</th>
                  <th>OWNER NAME</th>
                  <th>MOBILE</th>
                  <th>HEADQUARTER</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map((dealer) => (
                  <tr key={dealer.id}>
                    <td>{dealer.dealerShopName}</td>
                    <td>{dealer.ownerName}</td>
                    <td>{dealer.mobile}</td>
                    <td>
                      {hasHQ(dealer) ? (
                        dealer.headquarter?.name || `HQ #${dealer.headquarterId}`
                      ) : isApproved(dealer) ? (
                        <span style={{
                          padding: '2px 8px',
                          background: '#fff3e0',
                          color: '#e65100',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}>
                          ⚠️ Awaiting HQ
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`status-badge ${(dealer.status || '').toLowerCase()}`}>
                        {(dealer.status || 'PENDING').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {isPending(dealer) && isAdminRole && (
                          <button className="btn-action btn-approve"
                            onClick={() => askApprove(dealer)}>
                            Approve
                          </button>
                        )}
                        <button className="btn-action btn-view"
                          onClick={() => navigate(`/dealer/view/${dealer.id}`)}>
                          View
                        </button>
                        <button className="btn-action btn-edit"
                          onClick={() => navigate(`/dealer/edit/${dealer.id}`)}>
                          Edit
                        </button>
                        <button className="btn-action btn-delete"
                          onClick={() => askDelete(dealer)}>
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <span className="pagination-info">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} dealers
              </span>
              <div className="pagination-controls">
                <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                <button className="page-btn" onClick={() => setPage(prev => prev - 1)} disabled={page === 1}>‹</button>
                {getPageNumbers().map(p => (
                  <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>
                    {p}
                  </button>
                ))}
                <button className="page-btn" onClick={() => setPage(prev => prev + 1)} disabled={page === totalPages}>›</button>
                <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirm modal */}
      {confirmState.open && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12,
              padding: 24, maxWidth: 400, width: '100%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>
              {confirmState.action === 'approve' ? 'Approve Dealer' : 'Delete Dealer'}
            </h4>
            <p style={{ margin: '12px 0 20px 0', fontSize: 14, color: '#495057', lineHeight: 1.5 }}>
              {confirmState.action === 'approve' ? (
                <>Approve <strong>{confirmState.dealer?.dealerShopName}</strong>? They will become visible to the creator and admins.</>
              ) : (
                <>Delete <strong>{confirmState.dealer?.dealerShopName}</strong>? This cannot be undone.</>
              )}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={cancelConfirm}
                disabled={confirmState.loading}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: '1px solid #d1d5db',
                  background: '#fff', color: '#495057', fontWeight: 600, fontSize: 14,
                  cursor: confirmState.loading ? 'not-allowed' : 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={runConfirm}
                disabled={confirmState.loading}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: confirmState.action === 'delete' ? '#c62828' : '#2e7d32',
                  color: '#fff', fontWeight: 600, fontSize: 14,
                  cursor: confirmState.loading ? 'not-allowed' : 'pointer',
                  opacity: confirmState.loading ? 0.7 : 1,
                }}
              >
                {confirmState.loading
                  ? 'Processing...'
                  : confirmState.action === 'approve' ? 'Approve' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDealers;